/*
 * Copyright (C) 2024 Viasat, Inc.
 * All rights reserved.
 * The information in this software is subject to change without notice and
 * should not be construed as a commitment by Viasat, Inc.
 *
 * Viasat Proprietary
 * The Proprietary Information provided herein is proprietary to Viasat and
 * must be protected from further distribution and use. Disclosure to others,
 * use or copying without express written authorization of Viasat, is strictly
 * prohibited.
 */

import { useField, useFormikContext } from 'formik';
import { Box, TextField, TextFieldProps } from '@mui/material';
import { BeamInputLabel, BeamInputLabelProps } from './BeamInputLabel';
import React from 'react';
import { BeamHelpText } from './BeamHelpText';
import { AutofillWrapper } from './AutofillWrapper';

const inputStyle = {
  input: { color: '#ffffff', fontFamily: 'Source Sans Pro', marginLeft: '2px', backgroundColor: '#141d24' },
  textarea: { color: '#ffffff', fontFamily: 'Source Sans Pro' },
  '& .MuiOutlinedInput-root': {
    '& fieldset': {
      borderColor: '#9FAFBC',
    },
    '&:hover fieldset': {
      borderColor: 'white',
    },
    '&.Mui-focused fieldset': {
      borderColor: '#00C2DE',
    },
    '&.Mui-disabled fieldset': {
      borderColor: '#677A89 !important',
    },
    '&.Mui-disabled input': {
      color: '#677A89 !important',
      '-webkit-text-fill-color': '#677A89 !important',
    },
  },
  '& .MuiInputBase-input.Mui-disabled': {
    WebkitTextFillColor: 'gray',
  },
};

export interface BeamInputProps {
  name: string;
  placeholder?: string;
  helpText?: string;
  pattern?: string;
  disableInput?: boolean;
  multiline?: boolean;
}

export const BeamInput = ({
  name,
  label,
  placeholder,
  optional,
  required,
  sx,
  helpText,
  pattern,
  disableInput,
  multiline,
}: BeamInputProps & Partial<Omit<BeamInputLabelProps, 'htmlFor'>> & Partial<TextFieldProps>) => {
  const formikContext = useFormikContext();
  const [field, meta] = useField<string>(name);
  return (
    <Box display={'flex'} flexDirection={'column'} gap={'8px'} sx={sx}>
      {label && <BeamInputLabel label={label} optional={optional} required={required} htmlFor={name} />}
      <AutofillWrapper>
        <TextField
          multiline={multiline}
          minRows={2}
          sx={inputStyle}
          size="small"
          variant="outlined"
          fullWidth
          id={name}
          placeholder={placeholder}
          disabled={disableInput || formikContext.isSubmitting}
          error={meta.touched && meta.error !== undefined}
          helperText={meta.touched && meta.error}
          InputLabelProps={{ shrink: false }}
          InputProps={{
            slotProps: {
              input: {
                pattern,
              },
            },
          }}
          required={required}
          {...field}
        />
      </AutofillWrapper>
      <BeamHelpText helpText={helpText} />
    </Box>
  );
};
